import React, { useState } from 'react';
import { ExternalLink, BookOpen, Palette, FileText, ChevronRight } from 'lucide-react';
import { portfolioItems } from '../data/mock';

const PortfolioSection = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section id="portfolio" className="py-24 lg:py-32 bg-navy-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #C9A962 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-gold-500 font-semibold text-sm tracking-widest uppercase mb-4">
            Portfolio
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Criação de <span className="text-gold-500">Marcas</span>
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Conheça alguns dos projetos de branding e manuais de marca desenvolvidos pela DumanginADM
          </p>
          <div className="w-24 h-1 bg-gold-500 mx-auto mt-6" />
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {portfolioItems.map((project) => (
            <div 
              key={project.id}
              className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-gold-500/50 transition-all duration-500 hover:transform hover:scale-[1.02]"
            >
              {/* Color Preview */}
              <div className="h-32 flex">
                {project.colors.map((color, index) => (
                  <div 
                    key={index}
                    className="flex-1 transition-all duration-500 group-hover:flex-[1.2] first:group-hover:flex-[1]"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-gold-500 text-sm font-medium">{project.year}</span>
                  <span className="text-white/30">•</span>
                  <span className="text-white/50 text-sm">{project.industry}</span>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold-500 transition-colors duration-300">
                  {project.clientName}
                </h3>

                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  {project.description}
                </p>

                {/* Deliverables */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.deliverables.map((item, index) => (
                    <span 
                      key={index}
                      className="bg-white/5 border border-white/10 text-white/70 text-xs px-3 py-1 rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>

                {/* Action Button */}
                <button 
                  onClick={() => setSelectedProject(project)}
                  className="inline-flex items-center gap-2 text-gold-500 font-semibold text-sm hover:text-white transition-colors duration-300"
                >
                  Ver detalhes
                  <ChevronRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="mt-12 text-center">
          <p className="text-white/50 text-sm italic">
            * Projetos ilustrativos. Cases reais serão adicionados em breve.
          </p>
        </div>

        {/* What's Included Card */}
        <div className="mt-16 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 lg:p-12">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">
            O que inclui um <span className="text-gold-500">Manual de Marca</span>?
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Palette, title: "Identidade Visual", desc: "Logo, símbolo e variações" },
              { icon: BookOpen, title: "Paleta de Cores", desc: "Cores primárias e secundárias" },
              { icon: FileText, title: "Tipografia", desc: "Fontes e hierarquia textual" },
              { icon: ExternalLink, title: "Aplicações", desc: "Usos corretos e incorretos" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gold-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-gold-500" />
                </div>
                <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProject(null)}
        >
          <div 
            className="bg-navy-800 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Color Header */}
            <div className="h-24 flex rounded-t-2xl overflow-hidden">
              {selectedProject.colors.map((color, index) => (
                <div 
                  key={index}
                  className="flex-1 flex items-end justify-center pb-2"
                  style={{ backgroundColor: color }}
                >
                  <span className="text-[10px] font-mono text-white/80 bg-black/30 px-2 py-0.5 rounded">
                    {color}
                  </span>
                </div>
              ))}
            </div>

            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gold-500 font-medium">{selectedProject.year}</span>
                <span className="text-white/50">{selectedProject.industry}</span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-4">
                {selectedProject.clientName}
              </h3>

              <p className="text-white/70 leading-relaxed mb-6">
                {selectedProject.description}
              </p>

              <div className="mb-6">
                <h4 className="text-white font-semibold mb-3">Entregáveis:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.deliverables.map((item, index) => (
                    <span 
                      key={index}
                      className="bg-gold-500/10 border border-gold-500/30 text-gold-500 text-sm px-4 py-2 rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => setSelectedProject(null)}
                className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white py-3 rounded-xl font-medium transition-colors duration-300"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;
